import React, { FC, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { TTerritoryPostsContainerProps } from './constants';
import { TerritorySectionTitle } from '../TerritorySectionTitle/TerritorySectionTitle';
import { TerritoryGridContainer } from '../TerritoryGridContainer/TerritoryGridContainer';
import { PostCardContainer } from '../PostCard/PostCardContainer';
import { LoadMoreButton } from '../LoadMoreButton/LoadMoreButton';
import { SortingDropdown } from '../SortingDropdown/SortingDropdown';
import { ARTICLES_SORTING_DROPDOWN_ITEMS } from '../TerritoryArticlesSection/constants';
import { TerritoryEmptyStateContainer } from './TerritoryEmptyStateContainer';
import { getNewArticlePageUrl } from '../../services/pagesUrl/getNewArticlePageUrl';
import { PostCardLoader } from '../PostCard/PostCardLoader';
import { TerritorySectionTitleLoader } from '../TerritorySectionTitle/TerritorySectionTitleLoader';
import { hasPermissionToCreateTerritoryPostsAndPrompts } from '../WriteInTerritory/hooks/hasPermissionToCreateTerritoryPostsAndPrompts';
import { TerritorySeeAllButton } from '../TerritorySeeAllButton/TerritorySeeAllButton';

export const TerritoryExploreContent: FC<TTerritoryPostsContainerProps> = ({
  territoryId,
  articles,
  hasMoreArticlesToLoad,
  onLoadMoreWithToggle,
  isCollapsedSection,
  isLoading,
  showLoadMoreButton,
  canManagePinnedArticles,
  isPinningEnabled,
  sectionTitle,
  forceMobileView,
  gridColumns,
  hideSortingDropdown = false,
  onSortByChange,
  sortBy,
  isAuthLoading,
  territorySlug,
  territoryVersion,
  hideSeeAllButton = false,
  seeAllUrl,
}) => {
  const sectionTitleRef = useRef<HTMLDivElement>(null);
  const { hasCreatePostPermission } = hasPermissionToCreateTerritoryPostsAndPrompts(territoryId);

  if (isLoading || isAuthLoading) {
    return (
      <div>
        {isLoading || isAuthLoading ? (
          <TerritorySectionTitleLoader />
        ) : (
          <TerritorySectionTitle>{sectionTitle}</TerritorySectionTitle>
        )}
        <TerritoryGridContainer columns={gridColumns}>
          <>
            {Array.from({ length: forceMobileView ? 4 : 6 }, (_, index) => (
              <PostCardLoader key={`loader-${index}`} forceMobileView={forceMobileView} />
            ))}
          </>
        </TerritoryGridContainer>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between">
        {isLoading || isAuthLoading ? (
          <TerritorySectionTitleLoader />
        ) : (
          <TerritorySectionTitle ref={sectionTitleRef}>{sectionTitle}</TerritorySectionTitle>
        )}
        {!hideSeeAllButton && !isEmpty(articles) && (
          <div className="ml-auto mb-5">
            <TerritorySeeAllButton url={seeAllUrl} />
          </div>
        )}
        {!hideSortingDropdown && !isEmpty(articles) && (
          <SortingDropdown
            items={ARTICLES_SORTING_DROPDOWN_ITEMS}
            onSortByChange={onSortByChange}
            purpose="territory_articles"
            territoryId={territoryId}
            defaultSortBy={sortBy}
            dropdownMenuPosition="right"
            dropdownLabelbackgroundColor="transparent"
            dropdownLabelClassName="border border-t2-neutral-grey03"
          />
        )}
      </div>
      <>
        <TerritoryGridContainer columns={gridColumns}>
          {!isLoading && isEmpty(articles) ? (
            <div className="col-span-full">
              <TerritoryEmptyStateContainer
                title="Looking quiet here"
                description="Posts are great for longer content to share your thoughts or stories. Why not add one?"
                buttonTitle="Write New Post"
                buttonUrl={getNewArticlePageUrl({ territoryId })}
                buttonAs="a"
                canCreateInTerritory={hasCreatePostPermission}
                emptyStateType="post"
                territorySlug={territorySlug}
                territoryVersion={territoryVersion}
              />
            </div>
          ) : (
            articles?.map((article) => (
              <PostCardContainer
                key={article.id}
                article={article}
                canManagePinnedArticles={canManagePinnedArticles}
                isPinningEnabled={isPinningEnabled}
                forceMobileView={forceMobileView}
              />
            ))
          )}
        </TerritoryGridContainer>
        {showLoadMoreButton && (
          <div className="flex justify-center mt-4">
            <LoadMoreButton
              onClick={onLoadMoreWithToggle}
              hasMoreToLoad={hasMoreArticlesToLoad}
              isCollapsedSection={isCollapsedSection}
              isLoading={isLoading}
              sectionTitleRef={sectionTitleRef}
            />
          </div>
        )}
      </>
    </>
  );
};
